import { useEffect, useRef, useState } from "react";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import CameraAltIcon from "@mui/icons-material/AddAPhoto";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import PreviewIcon from "@mui/icons-material/Preview";
import CButton from "../../../basecomp/components/CButton";
import {
  fetchSizeOptions,
  getSampleUploadFile,
  uploadAssetDetailsData,
  fetchAssets,
  getAssetPdf,
  exportAssets,
  readPdfError,
} from "../model/AssetUpload";
import PrintSizeModal from "../view/PrintSizeModal";

const AssetUploadVM = () => {
  const { handleToastOpen } = useToast();
  const [sizeOptions, setSizeOptions] = useState([]);
  const [selectedSizeId, setSelectedSizeId] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [assetData, setAssetData] = useState([]);
  const [filteredAssetData, setFilteredAssetData] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [openCarousel, setOpenCarousel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fileKey, setFileKey] = useState(0); // Add this to force file input reset
  const [uploadErrorDetails, setUploadErrorDetails] = useState(null); // details for the "what is wrong" popup
  const [showRetired, setShowRetired] = useState(false); // "Show retired" switch
  const showRetiredRef = useRef(false); // same value, readable inside old listeners
  // Dropdown filters above the table ("" = all). Employee "__none__" = Not assigned
  const [filters, setFilters] = useState({ status: "", category: "", employee: "" });
  // "Choose sticker size" popup: { assetId, isPrint, type, missing_assets, missing_count }
  const [printSizeAsk, setPrintSizeAsk] = useState(null);

  const fetchAllAssets = async () => {
    try {
      const response = await fetchAssets(showRetiredRef.current);
      const assets = response.data || [];
      setAssetData(assets);
      setFilteredAssetData(assets); // Initialize filtered data
    } catch (error) {
      if (error.message) {
        handleToastOpen("error", error.message);
      }
      setAssetData([]);
      setFilteredAssetData([]);
    }
  };

  useEffect(() => {
    const fetchSize = async () => {
      try {
        const response = await fetchSizeOptions();
        setSizeOptions(response.data);
      } catch (error) {
        handleToastOpen("error", error.message);
        setSizeOptions([]);
      }
    };
    fetchSize();
    fetchAllAssets();
  }, []);

  // Reload the list when the user comes back to this tab
  // (e.g. after assigning/returning in the asset details tab)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchAllAssets();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // Reload the list when an asset is changed in another tab or window
  // (retire / restore on the asset page), even if this tab stays visible
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel("asset-updates");
      channel.onmessage = () => fetchAllAssets();
    } catch (e) {
      channel = null;
    }
    const handleStorage = (event) => {
      if (event.key === "asset_updated_at") fetchAllAssets();
    };
    const handleFocus = () => fetchAllAssets();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    return () => {
      if (channel) channel.close();
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Enhanced search functionality - search by asset_id, category, and employee_name
  useEffect(() => {
    // 1. Dropdown filters (Status / Category / Employee)
    const byFilters = assetData.filter((asset) => {
      if (filters.status && asset.asset_status !== filters.status) return false;
      if (filters.category && asset.category !== filters.category) return false;
      if (filters.employee === "__none__" && asset.employee_name) return false;
      if (
        filters.employee &&
        filters.employee !== "__none__" &&
        asset.employee_name !== filters.employee
      )
        return false;
      return true;
    });

    // 2. Search box (same as before)
    if (!searchQuery.trim()) {
      setFilteredAssetData(byFilters);
    } else {
      const filtered = byFilters.filter((asset) => {
        const searchTerm = searchQuery.toLowerCase();
        return (
          asset.asset_id?.toString().toLowerCase().includes(searchTerm) ||
          asset.category?.toLowerCase().includes(searchTerm) ||
          asset.employee_name?.toLowerCase().includes(searchTerm) ||
          asset.laptop_desktop_serial_no?.toString().toLowerCase().includes(searchTerm)
        );
      });
      setFilteredAssetData(filtered);
    }
  }, [searchQuery, assetData, filters]);

  // Values for the dropdowns (only what exists in the list)
  const uniqueSorted = (list) =>
    [...new Set(list.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const filterOptions = {
    status: uniqueSorted(assetData.map((a) => a.asset_status)),
    category: uniqueSorted(assetData.map((a) => a.category)),
    employee: uniqueSorted(assetData.map((a) => a.employee_name)),
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: "", category: "", employee: "" });
    setSearchQuery("");
  };

  const handleSampleFileDownload = async () => {
    try {
      setLoading(true)
      const response = await getSampleUploadFile("asset_details");
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Sample_Asset_Details_File.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      handleToastOpen("success", "File Downloaded Successfully");
    } catch (error) {
      handleToastOpen("error", "File download Unsuccessful");
    } finally{
      setLoading(false)
    }
  };

  const handleExportAssets = async () => {
    try {
      setLoading(true);
      const response = await exportAssets(searchQuery, showRetiredRef.current, filters);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Asset_Details_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      handleToastOpen("success", "File Exported Successfully");
    } catch (error) {
      if (error.response?.status === 404) {
        handleToastOpen("error", "No assets found to export");
      } else {
        handleToastOpen("error", "Export Unsuccessful");
      }
    } finally {
      setLoading(false);
    }
  };

  // "Show retired" switch: reload the list with / without retired assets
  const handleShowRetiredChange = (event) => {
    const checked = event.target.checked;
    showRetiredRef.current = checked;
    setShowRetired(checked);
    fetchAllAssets();
  };

  const handleFileChoose = (files) => {
    setSelectedFiles(files);
  };

  const resetFileInput = () => {
    setSelectedFiles([]);
    setFileKey((prev) => prev + 1); // This will force the file input to reset
  };

  const handleAssetDetailsFileUpload = async () => {
    try {
      setLoading(true)
      if (selectedSizeId?.length === 0) {
        handleToastOpen("error", "Please select the size");
        return;
      }

      if (selectedFiles?.length === 0) {
        handleToastOpen("error", "Please select a file");
        return;
      }

      const response = await uploadAssetDetailsData(
        selectedSizeId,
        selectedFiles
      );

      if (response && response.data) {
        handleToastOpen("success", response.data.message);

        if (response.status === 200) {
          // Show summary if available
          if (response.data.summary) {
            const { successful_inserts, duplicate_records, failed_inserts } =
              response.data.summary;
            console.log(
              `Summary: ${successful_inserts} uploaded, ${duplicate_records} duplicates, ${failed_inserts} failed`
            );
          }

          // Reset file input and refresh data
          resetFileInput();
          await fetchAllAssets(); // This will update both assetData and filteredAssetData
        } else if (response.status === 404 || response.status === 400) {
          handleToastOpen("error", response.data.message);
          // Reset file input even on error so user can try again
          resetFileInput();
        }
      }
    } catch (error) {
      // Reset file input on any error so user can try again
      resetFileInput();

      const errData = error.response?.data;
      if (errData?.row_errors?.length > 0 || errData?.missing_columns?.length > 0) {
        // Show the full list of problems in a popup
        setUploadErrorDetails(errData);
        handleToastOpen("error", "Upload failed. Please check the problems in the file.");
      } else if (error.response) {
        handleToastOpen(
          "error",
          error.response.data?.message || `Error ${error.response.status}`
        );
      } else if (error.request) {
        handleToastOpen("error", "No response from server. Please try again.");
      } else {
        handleToastOpen("error", error.message);
      }
    } finally{
      setLoading(false)
    }
  };

  const handleSizeChange = (value) => {
    setSelectedSizeId(value.id);
  };

  const handleImageClick = (actualRow) => {
    const hasNoImages = actualRow.images.length === 0;
    if (hasNoImages) {
      const url = `/asset-it/image-upload?id=${actualRow.asset_id}`;
      window.open(url, "_blank");
    } else {
      setCarouselImages(actualRow.images);
      setOpenCarousel(true);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const getFormattedDateTime = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().slice(0, 10);
    const currentTime = currentDate.toLocaleTimeString();
    return formattedDate + "_" + currentTime;
  };

  const pdfPrintView = async (assetId, isPrint, type, sizeId, saveSize) => {
    try {
      setLoading(true)
      const response = await getAssetPdf(assetId, type, sizeId, saveSize);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      if (isPrint) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `asset_${assetId || "all"}_${getFormattedDateTime()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (error) {
      const { status, data } = await readPdfError(error);
      if (status === 409 && data?.need_size) {
        // Some assets have no sticker size -> ask the user which size to use
        setPrintSizeAsk({ assetId, isPrint, type, ...data });
      } else {
        handleToastOpen("error", data?.message || error.message);
      }
    } finally{
      setLoading(false)
    }
  };

  // Size chosen in the popup -> print again with that size
  const handlePrintSizeConfirm = async (sizeId, saveSize) => {
    const ask = printSizeAsk;
    setPrintSizeAsk(null);
    if (!ask) return;
    await pdfPrintView(ask.assetId, ask.isPrint, ask.type, sizeId, saveSize);
    if (saveSize) handleToastOpen("success", "Sticker size saved for these assets");
  };

  const printSizeModal = (
    <PrintSizeModal
      open={!!printSizeAsk}
      missingAssets={printSizeAsk?.missing_assets || []}
      missingCount={printSizeAsk?.missing_count || 0}
      sizeOptions={sizeOptions}
      onCancel={() => setPrintSizeAsk(null)}
      onConfirm={handlePrintSizeConfirm}
    />
  );

  const handlePrintAll = () => {
    pdfPrintView(undefined, true, "print_view_all");
  };

  const infoHeadData = [
    "Asset ID",
    "Category",
    "Employee Name",
    "Images",
    "Preview",
    <CButton
      key="print-all-btn"
      cSrc={<LocalPrintshopIcon />}
      cTooltipText="Print All Assets"
      cOnClick={handlePrintAll}
    />,
  ];

  const infoHeadData2 = [
    "Asset ID",
    "Category",
    "Employee Name",
    "Images",
    "Preview",
    <CButton key="print-all-btn" cSrc={<LocalPrintshopIcon />} />,
  ];

  const infoRowData = [
    {
      value: `asset_id`,
      type: "T",
    },
    {
      value: `category`,
      type: "T",
    },
    {
      value: `employee_name`,
      type: "T",
    },
    {
      value: (actualRow) =>
        actualRow.images.length === 0 ? <CameraAltIcon /> : <PreviewIcon />,
      tooltipText: (actualRow) =>
        actualRow.images.length === 0
          ? "Upload Asset Images"
          : "View Asset Images",
      type: "IC",
      actionV: (actualRow) => handleImageClick(actualRow),
    },
    {
      value: <PictureAsPdfIcon />,
      tooltipText: "View Asset PDF",
      type: "I",
      actionV: (actualRow) =>
        pdfPrintView(actualRow.asset_id, false, "print_view_id"),
    },
    {
      value: <LocalPrintshopIcon />,
      tooltipText: "Print Asset PDF",
      type: "I",
      actionV: (actualRow) =>
        pdfPrintView(actualRow.asset_id, true, "print_view_id"),
    },
  ];

  return {
    printSizeModal,
    filters,
    filterOptions,
    handleFilterChange,
    clearFilters,
    totalAssetCount: assetData.length,
    showRetired,
    handleShowRetiredChange,
    uploadErrorDetails,
    setUploadErrorDetails,
    fetchAllAssets,
    handleExportAssets,
    handleSampleFileDownload,
    handleFileChoose,
    handleAssetDetailsFileUpload,
    sizeOptions,
    handleSizeChange,
    assetData: filteredAssetData, // Return filtered data instead of original
    infoHeadData,
    infoRowData,
    carouselImages,
    openCarousel,
    setOpenCarousel,
    handlePrintAll,
    pdfPrintView,
    searchQuery,
    handleSearchChange,
    clearSearch,
    fileKey, // Export this for the file input component
    resetFileInput,
    infoHeadData2,
    loading,
    handleImageClick
  };
};

export default AssetUploadVM;